import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save } from 'lucide-react';
import { createPessoa, getPessoa, Pessoa, updatePessoa } from '../api/pessoa';
import { apiError } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import FormField from '../components/FormField';
import CepLookupField from '../components/CepLookupField';
import CnpjLookupField from '../components/CnpjLookupField';

type FormValues = Partial<Pessoa>;

export default function PessoaEditPage() {
  const { id } = useParams();
  const isNew = !id || id === 'novo';
  const pid = isNew ? null : Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const q = useQuery({
    enabled: !isNew && !!pid,
    queryKey: ['pessoa', pid],
    queryFn: () => getPessoa(pid!)
  });

  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: { tipo_doc: 'CNPJ' }
  });

  useEffect(() => {
    if (q.data) reset(q.data);
  }, [q.data, reset]);

  const tipoDoc = watch('tipo_doc') || 'CNPJ';
  const cepVal = watch('cep') || '';
  const docVal = watch('documento') || '';

  async function onSubmit(values: FormValues) {
    setSaving(true);
    setErr(null);
    try {
      // NT 2026.004: CNPJ pode ser alfanumerico. Preserva letras + uppercase.
      // CEP continua sendo numerico.
      const payload = {
        ...values,
        documento: (values.documento || '').toUpperCase().trim(),
        cep: (values.cep || '').replace(/\D/g, ''),
      };
      const saved = isNew ? await createPessoa(payload) : await updatePessoa(pid!, payload);
      qc.invalidateQueries({ queryKey: ['pessoas'] });
      navigate(`/pessoas/${saved.id}`, { replace: true });
    } catch (e) {
      setErr(apiError(e, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  }

  if (!isNew && q.isLoading) {
    return <div className="py-10 grid place-items-center"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <header className="flex items-center gap-2">
        <Link to="/pessoas" className="btn-ghost !px-2 !py-1 h-9 w-9">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <div className="eyebrow">Cadastro</div>
          <h1 className="h-display text-2xl md:text-3xl text-slate-950 dark:text-white">
            {isNew ? 'Nova pessoa' : 'Editar pessoa'}
          </h1>
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <section className="card-surface p-5 space-y-4">
          <h2 className="eyebrow">Identificação</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="field-label">Tipo</label>
              <select className="field-input" {...register('tipo_doc')}>
                <option value="CNPJ">Jurídica (CNPJ)</option>
                <option value="CPF">Física (CPF)</option>
                <option value="EX">Exterior</option>
              </select>
            </div>
            {tipoDoc === 'CNPJ' ? (
              <CnpjLookupField
                label="CNPJ"
                value={docVal}
                onChange={(v) => setValue('documento', v)}
                onAutofill={(data) => {
                  if (data.razao_social) setValue('nome', data.razao_social);
                  if (data.nome_fantasia) setValue('nome_fantasia', data.nome_fantasia);
                  if (data.cep) setValue('cep', data.cep);
                  if (data.logradouro) setValue('logradouro', data.logradouro);
                  if (data.numero) setValue('numero', data.numero);
                  if (data.bairro) setValue('bairro', data.bairro);
                  if (data.complemento) setValue('complemento', data.complemento);
                  if (data.uf) setValue('uf', data.uf);
                  if (data.municipio) setValue('xmun', data.municipio);
                  if (data.codigo_municipio_ibge) setValue('cmun', String(data.codigo_municipio_ibge));
                  if (data.email) setValue('email', data.email);
                  if (data.ddd_telefone_1) setValue('telefone', data.ddd_telefone_1);
                }}
                className="md:col-span-2"
              />
            ) : (
              <FormField label="Documento (CPF/passaporte)" {...register('documento')} className="md:col-span-2" />
            )}
            <FormField label="Nome / Razão social" className="md:col-span-2" {...register('nome')} />
            <FormField label="Nome fantasia" {...register('nome_fantasia')} />
            <FormField label="Inscrição Estadual" {...register('ie')} />
            <FormField label="E-mail" {...register('email')} />
            <FormField label="Telefone" {...register('telefone')} />
          </div>
        </section>

        <section className="card-surface p-5 space-y-4">
          <h2 className="eyebrow">Endereço</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <CepLookupField
              value={cepVal}
              onChange={(v) => setValue('cep', v)}
              onAutofill={(data) => {
                if (data.logradouro) setValue('logradouro', data.logradouro);
                if (data.bairro) setValue('bairro', data.bairro);
                if (data.localidade) setValue('xmun', data.localidade);
                if (data.uf) setValue('uf', data.uf);
                if (data.ibge) setValue('cmun', data.ibge);
              }}
            />
            <FormField label="Logradouro" className="md:col-span-3" {...register('logradouro')} />
            <FormField label="Número" {...register('numero')} />
            <FormField label="Bairro" className="md:col-span-2" {...register('bairro')} />
            <FormField label="Complemento" {...register('complemento')} />
            <FormField label="Código Município (IBGE)" {...register('cmun')} />
            <FormField label="Município" className="md:col-span-2" {...register('xmun')} />
            <FormField label="UF" {...register('uf')} maxLength={2} />
          </div>
        </section>

        {err && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Link to="/pessoas" className="btn-secondary">Cancelar</Link>
          <button type="submit" disabled={saving} className="btn-primary">
            <Save className="h-4 w-4" />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
